/**
 * Loading / error / unavailable / content for one cached Stats → Overview block (A7Y-5020).
 *
 * A failed block renders only its error, and an `unavailable` block renders only its reason: no zero and no
 * row from an earlier or partial response is ever shown beside either. Not `BlockPanel`: that one's only
 * non-ok state is `no-counted-rows`, whose text is wrong for a snapshot that has not been written yet.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import type { StatsOverviewBlock, StatsOverviewBlockMeta } from '@a7y/types';
import { formatUtcInstant } from '../format';

function Heading({ title }: { title: string }) {
  return <h2 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-white">{title}</h2>;
}

export function BlockMetaLine({ meta, testId }: { meta: StatsOverviewBlockMeta; testId: string }) {
  return (
    <p data-testid={testId} className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
      Live figures read {formatUtcInstant(meta.generatedAt)} from {meta.readFrom}; source: {meta.source}; cached up
      to {Math.round(meta.cacheTtlSec / 60)} min.
    </p>
  );
}

export function OverviewBlockPanel<T>({
  title,
  testId,
  query,
  children,
}: {
  title: string;
  testId: string;
  query: UseQueryResult<StatsOverviewBlock<T>>;
  children: (body: T, block: StatsOverviewBlock<T>) => React.ReactNode;
}) {
  const block = query.data;
  let content: React.ReactNode;
  if (query.isError) {
    content = (
      <div
        role="alert"
        data-testid={`${testId}-error`}
        className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
      >
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {title} failed to load. Refresh retries it; the Worker logs name the failing query.
      </div>
    );
  } else if (!block) {
    content = <div className="p-6 text-sm text-zinc-500">Loading {title}…</div>;
  } else if (block.state === 'unavailable') {
    content = (
      <div
        data-testid={`${testId}-unavailable`}
        className="flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-200"
      >
        <Info className="h-4 w-4 shrink-0" />
        {title} unavailable: {block.reason}.
      </div>
    );
  } else {
    content = children(block.body, block);
  }
  return (
    <section data-testid={testId} className="mb-8">
      <Heading title={title} />
      {content}
    </section>
  );
}
